import React from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { Layout as AntLayout, Menu, Avatar, Dropdown, Space, notification, Drawer, Button, message, Tag } from 'antd'
import type { MenuProps } from 'antd'
import { 
  BookOutlined, 
  ImportOutlined, 
  SearchOutlined, 
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuOutlined,
  HeartOutlined,
  QuestionCircleOutlined,
  SoundOutlined
} from '@ant-design/icons'
import { useAuthStore } from '../stores/auth'
import { computeApiBaseURL, request, fetchAuthMeCached, invalidateAuthMeCache } from '../utils/request'
import { getUserAvatarSrc, isAvatarSelectionDirty, markAvatarSelectionSynced, readAvatarSelection } from '../utils/avatar'

const { Header, Sider, Content } = AntLayout
const DESKTOP_HEADER_HEIGHT = 64

const Layout: React.FC = () => {
  const location = useLocation()
  const { user, logout, updateUser, isAuthenticated } = useAuthStore()
  const [canUpload, setCanUpload] = React.useState<boolean>(false)
  const [hasUploadedBooks, setHasUploadedBooks] = React.useState<boolean>(false)
  const [siteName, setSiteName] = React.useState('伴读么 - Ai角色朗读电子书屋')
  const [avatarSrc, setAvatarSrc] = React.useState<string | null>(null)
  const avatarSyncAttemptedRef = React.useRef<string>('')

  React.useEffect(() => {
    const shouldHandleUserAccessChange = (evt: Event): boolean => {
      const detail = (evt as CustomEvent<{ userId?: string | number }>).detail
      const evtUserId = detail?.userId
      if (evtUserId == null || evtUserId === '') return true
      if (!user?.id) return false
      return String(evtUserId) === String(user.id)
    }

    const loadProfile = async (force = false) => {
      const userId = user?.id
      if (!isAuthenticated || !userId) {
        return
      }
      try {
        const res = await fetchAuthMeCached({ skipRedirect: false, force })
        const raw = (res as { data?: { user?: unknown; data?: unknown } }).data?.user || (res as { data?: { user?: unknown; data?: unknown } }).data?.data
        if (raw && typeof raw === 'object') {
          const payload = raw as { createdAt?: string; createTime?: string }
          const next = { ...(payload as unknown as Record<string, unknown>), createdAt: payload.createdAt || payload.createTime }
          updateUser(next)
        }
        try {
          const key = String(userId)
          if (avatarSyncAttemptedRef.current !== key && isAvatarSelectionDirty(userId)) {
            avatarSyncAttemptedRef.current = key
            void (async () => {
              try {
                const sel = readAvatarSelection(userId)
                await request.put('/api/auth/avatar', { avatar: sel }, { headers: { 'X-Silent': '1', 'X-Skip-401-Redirect': '1' } })
                markAvatarSelectionSynced(userId)
              } catch {
                void 0
              }
            })()
          }
        } catch {
          void 0
        }
      try {
        const vip = await request.get('/api/vip/info', { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } })
        const vdata = vip.data?.data || vip.data
        if (vdata) {
          const update: Record<string, unknown> = {}
          if (typeof vdata.points === 'number') update.points = Number(vdata.points)
          if (vdata.levelName) update.levelName = vdata.levelName
          if (vdata.rank) update.rank = vdata.rank
          if (Object.keys(update).length > 0) updateUser(update)
        }
      } catch {
        void 0
      }
    } catch {
      void 0
    }
  }

    const refresh = async () => {
      try {
        const res = await request.get('/api/auth/public-config', { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } })
        if (res.data?.success) {
          setSiteName(res.data.data.site_name || '伴读么 - Ai角色朗读电子书屋')
        }
      } catch {
        void 0
      }

      if (!isAuthenticated) {
        setCanUpload(false)
        setHasUploadedBooks(false)
        return
      }

      try {
        const res = await request.get('/api/upload/settings', { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } })
        const data = res.data?.data || {}
        setCanUpload(Boolean(data.canUpload))
      } catch {
        setCanUpload(false)
      }
      try {
        const res = await request.get('/api/upload/user-books', {
          params: { page: 1, pageSize: 1 },
          headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' }
        })
        const total = res.data?.pagination?.total
        if (typeof total === 'number') {
          setHasUploadedBooks(total > 0)
        } else {
          const list = res.data?.data ?? res.data ?? []
          setHasUploadedBooks(Array.isArray(list) && list.length > 0)
        }
      } catch {
        setHasUploadedBooks(false)
      }
    }
    const onCfg = () => { refresh() }
    const onPerm = (evt: Event) => {
      if (!shouldHandleUserAccessChange(evt)) return
      invalidateAuthMeCache()
      void loadProfile(true)
      void refresh()
    }
    loadProfile()
    refresh()
    window.addEventListener('ebookhub:config-changed', onCfg as EventListener)
    window.addEventListener('ebookhub:permission-changed', onPerm as EventListener)
    return () => {
      window.removeEventListener('ebookhub:config-changed', onCfg as EventListener)
      window.removeEventListener('ebookhub:permission-changed', onPerm as EventListener)
    }
  }, [isAuthenticated, user?.id, updateUser])

  React.useEffect(() => {
    setAvatarSrc(getUserAvatarSrc(user?.id, user?.username))
  }, [user?.id, user?.username])

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ userId?: string | number }>
      const evtUserId = ce?.detail?.userId
      if (!evtUserId || !user?.id) return
      if (String(evtUserId) !== String(user.id)) return
      setAvatarSrc(getUserAvatarSrc(user.id, user.username))
    }
    window.addEventListener('ebookhub:avatar-changed', handler as EventListener)
    return () => window.removeEventListener('ebookhub:avatar-changed', handler as EventListener)
  }, [user?.id, user?.username])
  const navigate = useNavigate()
  React.useEffect(() => {
    let es: EventSource | null = null
    let reconnectTimer: NodeJS.Timeout | null = null
    let reconnectAttempts = 0
    let onlineListener: ((this: Window, ev: Event) => unknown) | null = null
    const baseReconnectDelayMs = 2000
    const maxReconnectDelayMs = 30000
    const jitterMs = 800

    // 只在用户登录时连接SSE
    if (!user) {
      return
    }
    if (typeof window === 'undefined' || typeof (window as unknown as { EventSource?: unknown }).EventSource !== 'function') {
      return
    }

    const clearReconnectTimer = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
    }

    const connectSSE = () => {
      try {
        // 关闭现有连接
        if (es) {
          es.close()
          es = null
        }

        const apiBase = String(computeApiBaseURL()).replace(/\/$/, '')
        const token = (typeof localStorage !== 'undefined' ? (localStorage.getItem('token') || '') : '')
        const qs = token ? `?token=${encodeURIComponent(token)}` : ''
        const url = `${apiBase}/api/notifications/stream${qs}`
        const tryConnect = () => {
          try {
            if (es) { es.close(); es = null }
            es = new EventSource(url)
            es.onopen = () => {
              reconnectAttempts = 0
              clearReconnectTimer()
            }
            es.onmessage = (e) => {
              try {
                const payload = JSON.parse(e.data)
                if (payload?.type === 'notification') {
                  const { title, content, level } = payload.data || {}
                  const lvl = String(level || 'info').toLowerCase()
                  const msgTitle = title || '系统通知'
                  const desc = content || ''
                  if (lvl === 'error') {
                    notification.open({ message: msgTitle, description: desc, type: 'error', duration: 5 })
                  } else if (lvl === 'warn' || lvl === 'warning') {
                    message.warning({ content: desc ? `${msgTitle}：${desc}` : msgTitle, duration: 3, key: 'global_warn' })
                  } else {
                    message.info({ content: desc ? `${msgTitle}：${desc}` : msgTitle, duration: 2, key: 'global_info' })
                  }
                } else if (payload?.type === 'heartbeat') {
                  void 0
                } else if (payload?.type === 'config_changed') {
                  try { window.dispatchEvent(new CustomEvent('ebookhub:config-changed', { detail: payload.data })) } catch { /* noop */ }
                } else if (payload?.type === 'permission_changed') {
                  try { window.dispatchEvent(new CustomEvent('ebookhub:permission-changed', { detail: payload.data })) } catch { /* noop */ }
                } else if (payload?.type === 'voice_roles_changed') {
                  try { window.dispatchEvent(new CustomEvent('ebookhub:voice-roles-changed', { detail: payload.data })) } catch { /* noop */ }
                } else if (payload?.type === 'points_awarded') {
                  const d = payload.data || {}
                  const mine = String(d.userId || '') === String(user?.id || '')
                  if (mine) {
                    try { useAuthStore.getState().updateUser({ points: Number(d.balance || 0) }) } catch { /* noop */ }
                    notification.open({ message: '积分奖励', description: `获得积分 ${d.points}`, type: 'info' })
                  }
                }
              } catch (err) { void err }
            }
            const scheduleReconnect = () => {
              clearReconnectTimer()
              if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                if (!onlineListener) {
                  onlineListener = () => {
                    try {
                      if (onlineListener) {
                        window.removeEventListener('online', onlineListener as EventListener)
                      }
                    } catch { /* noop */ }
                    onlineListener = null
                    reconnectAttempts = 0
                    tryConnect()
                  }
                  try { window.addEventListener('online', onlineListener as EventListener) } catch { /* noop */ }
                }
                return
              }

              const exp = Math.min(10, Math.max(0, reconnectAttempts))
              const delay = Math.min(maxReconnectDelayMs, Math.floor(baseReconnectDelayMs * Math.pow(2, exp)))
              const jitter = Math.floor(Math.random() * jitterMs)
              reconnectAttempts += 1
              reconnectTimer = setTimeout(() => { tryConnect() }, delay + jitter)
            }
            es.onerror = (_e) => {
              void _e
              if (es) { try { es.close() } catch { /* noop */ } es = null }
              scheduleReconnect()
            }
          } catch (err) { void err }
        }
        tryConnect()
      } catch (err) {
        void err
      }
    }

    // 初始连接
    connectSSE()

    // 清理函数
    return () => {
      clearReconnectTimer()
      if (onlineListener) {
        try { window.removeEventListener('online', onlineListener as EventListener) } catch { /* noop */ }
        onlineListener = null
      }
      if (es) {
        es.close()
        es = null
      }
    }
  }, [user]) // 依赖user，当用户状态变化时重新连接

  const canManageUploadsMenu = React.useMemo(() => {
    if (!user) return false
    return canUpload || hasUploadedBooks
  }, [user, canUpload, hasUploadedBooks])

  const currentSelectedKey = React.useMemo(() => {
    if (location.pathname === '/profile') {
      const search = location.search || ''
      if (search.includes('tab=uploads')) {
        return '/profile/uploads'
      }
    }
    if (location.pathname === '/favorites') {
      return '/favorites'
    }
    return location.pathname
  }, [location.pathname, location.search])

  const menuItems: MenuProps['items'] = [
    {
      key: '/',
      icon: <BookOutlined />,
      label: <Link to="/">书架</Link>,
    },
    ...(canUpload ? [{ key: '/import', icon: <ImportOutlined />, label: <Link to="/import">导入书籍</Link> }] : []),
    {
      key: '/search',
      icon: <SearchOutlined />,
      label: <Link to="/search">搜索</Link>,
    },
    {
      key: '/tts',
      icon: <SoundOutlined />,
      label: <Link to="/tts">文本转语音</Link>,
    },
    {
      key: '/favorites-root',
      icon: <HeartOutlined />,
      label: '我的收藏',
      children: [
        {
          key: '/favorites',
          label: <Link to="/favorites">收藏列表</Link>
        },
        ...(canManageUploadsMenu ? [{
          key: '/profile/uploads',
          label: <Link to="/profile?tab=uploads">上传的书</Link>
        }] : [])
      ]
    },
    {
      key: '/help',
      icon: <QuestionCircleOutlined />,
      label: <Link to="/help">帮助</Link>,
    },
  ]

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
      onClick: () => navigate('/profile')
    },
    {
      key: 'favorites',
      icon: <UserOutlined />,
      label: '我的收藏',
      onClick: () => navigate('/favorites')
    },
    ...(user?.role === 'admin' || user?.role === 'super_admin' ? [
      {
        key: 'admin',
        icon: <SettingOutlined />,
        label: '后台管理',
        onClick: () => navigate('/admin')
      }
    ] : []),
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: logout,
    },
  ];

  const isReaderRoute = location.pathname.startsWith('/reader')
  const isHomeRoute = location.pathname === '/'
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const logoUrl = `${import.meta.env.BASE_URL}icons/My_logo.png`
  const brandImgUrl = `${import.meta.env.BASE_URL}icons/bandume800x333.png`

  return (
    <AntLayout style={{ minHeight: '100vh', background: 'var(--color-bg-body)' }}>
      {!isReaderRoute && (
        <Header className="glass-panel sticky top-0 z-50 h-16 px-4 md:px-6 flex items-center justify-between shadow-sm border-b border-white/50">
          <div className="flex items-center h-full">
            <Button type="text" className="md:hidden mr-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50" icon={<MenuOutlined />} onClick={() => setMobileOpen(true)} />
            <Link to="/" className="flex items-center h-full py-2 hover:opacity-90 transition-opacity">
              <img src={logoUrl} alt={siteName} className="w-auto h-full mr-3 drop-shadow-sm" style={{ objectFit: 'contain', maxHeight: '40px' }} />
              <img
                src={brandImgUrl}
                alt={siteName}
                className="w-auto h-full"
                style={{ objectFit: 'contain', maxHeight: '40px' }}
              />
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" overlayClassName="shadow-theme-lg rounded-xl">
              <Space className="cursor-pointer hover:bg-white/50 px-2 py-1 rounded-lg transition-colors border border-transparent hover:border-white/60">
                <Avatar src={avatarSrc || undefined} icon={<UserOutlined />} className="bg-gradient-to-br from-primary-400 to-primary-600 text-white shadow-md" />
                <span className="hidden md:inline font-medium text-gray-700">{user?.username || '用户'}</span>
                {user?.levelName && <Tag color="#f59e0b" className="mr-0 border-none shadow-sm font-bold">{user.levelName}</Tag>}
              </Space>
            </Dropdown>
          </div>
        </Header>
      )}
      
      <AntLayout style={{ background: 'transparent' }}>
        {!isReaderRoute && (
          <Sider
            width={240}
            className="hidden md:block z-10"
            theme="light"
            style={{
              background: 'rgba(255,255,255,0.4)',
              borderRight: '1px solid rgba(255,255,255,0.5)',
              backdropFilter: 'blur(8px)',
              position: 'sticky',
              top: DESKTOP_HEADER_HEIGHT,
              alignSelf: 'flex-start',
              height: `calc(100vh - ${DESKTOP_HEADER_HEIGHT}px)`,
              overflow: 'hidden'
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={[currentSelectedKey]}
              items={menuItems}
              className="h-full border-r-0 py-4 custom-menu bg-transparent"
              style={{ background: 'transparent', height: '100%', overflowY: 'auto', overflowX: 'hidden' }}
            />
          </Sider>
        )}
        
        <Content
          className={
            isReaderRoute
              ? 'p-0 m-0'
              : (isHomeRoute ? 'mx-2 mb-2 mt-0.25 px-2 pb-2 pt-0.5 md:mx-4 md:mb-4 md:mt-1 md:px-4 md:pb-4 md:pt-2 transition-all duration-300' : 'm-3 p-4 md:m-6 md:p-6 transition-all duration-300')
          }
        >
          <Outlet />
        </Content>
      </AntLayout>

      {!isReaderRoute && (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          placement="left"
          width={280}
          styles={{ header: { borderBottom: '1px solid var(--color-primary-50)' }, body: { padding: 0 } }}
          title={
            <div className="flex items-center justify-center py-2">
              <img src={logoUrl} alt={siteName} className="w-8 h-8 mr-2" style={{ objectFit: 'contain' }} />
              <img src={brandImgUrl} alt={siteName} className="w-auto h-8" style={{ objectFit: 'contain' }} />
            </div>
          }
        >
          <Menu
            mode="inline"
            selectedKeys={[currentSelectedKey]}
            items={menuItems}
            onClick={() => setMobileOpen(false)}
            className="border-none py-2"
          />
        </Drawer>
      )}

    </AntLayout>
  )
}

export default Layout
